import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import {
    Plus,
    Pencil,
    Trash2,
    X,
    Save,
    Package,
    Power,
    RefreshCw
} from "lucide-react";

import toast from "react-hot-toast";

import api from "../api";

// ============================================================
// API
// ============================================================

const CATEGORIES_API =
    "/categories-prestations";

const PRESTATIONS_API =
    "/prestations";

// ============================================================
// CATÉGORIE EXCLUE DU CATALOGUE
// ============================================================
//
// L'hébergement est géré par les modules dédiés :
// appartements, réservations, séjours, tarifs, etc.
//
// On conserve la catégorie en base car elle est utilisée
// par l'ancien système, mais elle n'apparaît pas dans cette
// interface.
//
// ============================================================

const CATEGORIE_HEBERGEMENT =
    "Hébergement";

// ============================================================
// COMPOSANT
// ============================================================

export default function PrestationsGestion() {

    // ========================================================
    // CATÉGORIES
    // ========================================================

    const [
        categories,
        setCategories
    ] = useState([]);

    const [
        selectedCategorieId,
        setSelectedCategorieId
    ] = useState(null);

    const [
        loadingCategories,
        setLoadingCategories
    ] = useState(true);

    // ========================================================
    // PRESTATIONS
    // ========================================================

    const [
        prestations,
        setPrestations
    ] = useState([]);

    const [
        loadingPrestations,
        setLoadingPrestations
    ] = useState(false);

    // ========================================================
    // MODAL CATÉGORIE
    // ========================================================

    const [
        categorieModal,
        setCategorieModal
    ] = useState(false);

    const [
        editingCategorie,
        setEditingCategorie
    ] = useState(null);

    const [
        categorieNom,
        setCategorieNom
    ] = useState("");

    const [
        savingCategorie,
        setSavingCategorie
    ] = useState(false);

    // ========================================================
    // MODAL PRESTATION
    // ========================================================

    const [
        prestationModal,
        setPrestationModal
    ] = useState(false);

    const [
        editingPrestation,
        setEditingPrestation
    ] = useState(null);

    const [
        prestationForm,
        setPrestationForm
    ] = useState({
        categorie_id: "",
        nom: "",
        nom_fr: "",
        nom_en: "",
        prix_defaut: "",
        actif: true
    });

    const [
        savingPrestation,
        setSavingPrestation
    ] = useState(false);

    // ========================================================
    // CHARGEMENT
    // ========================================================

    useEffect(() => {
        loadCategories();
    }, []);

    // ========================================================
    // CATÉGORIES VISIBLES
    // ========================================================

    const categoriesVisibles = useMemo(() => {

        return categories.filter(
            category =>
                String(
                    category.nom
                ).trim().toLowerCase() !==
                CATEGORIE_HEBERGEMENT
                    .toLowerCase()
        );

    }, [categories]);

    // ========================================================
    // CATÉGORIE SÉLECTIONNÉE
    // ========================================================

    const selectedCategorie =
        useMemo(() => {

            return categoriesVisibles.find(
                category =>
                    Number(
                        category.id
                    ) ===
                    Number(
                        selectedCategorieId
                    )
            );

        }, [
            categoriesVisibles,
            selectedCategorieId
        ]);

    // ========================================================
    // CHARGER CATÉGORIES
    // ========================================================

    const loadCategories = async () => {

        setLoadingCategories(true);

        try {

            const response =
                await api.get(
                    CATEGORIES_API
                );

            const data =
                Array.isArray(
                    response.data
                )
                    ? response.data
                    : Array.isArray(
                        response.data?.data
                    )
                        ? response.data.data
                        : [];

            setCategories(data);

            const visibles =
                data.filter(
                    category =>
                        String(
                            category.nom
                        ).trim().toLowerCase() !==
                        CATEGORIE_HEBERGEMENT
                            .toLowerCase()
                );

            if (
                visibles.length === 0
            ) {

                setSelectedCategorieId(
                    null
                );

                return;
            }

            const currentExists =
                visibles.some(
                    category =>
                        Number(
                            category.id
                        ) ===
                        Number(
                            selectedCategorieId
                        )
                );

            if (
                !currentExists
            ) {

                // ------------------------------------------------
                // On privilégie Café & Bar si présent.
                // ------------------------------------------------

                const cafeBar =
                    visibles.find(
                        category =>
                            String(
                                category.nom
                            ).trim().toLowerCase() ===
                            "café & bar"
                    );

                setSelectedCategorieId(
                    cafeBar
                        ? cafeBar.id
                        : visibles[0].id
                );
            }

        } catch (error) {

            console.error(
                "Erreur chargement catégories:",
                error
            );

            toast.error(
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Impossible de charger les catégories."
            );

        } finally {

            setLoadingCategories(
                false
            );
        }
    };

    // ========================================================
    // CHARGER PRESTATIONS
    // ========================================================

    const loadPrestations = async () => {

        if (
            !selectedCategorieId
        ) {
            setPrestations([]);
            return;
        }

        setLoadingPrestations(
            true
        );

        try {

            const response =
                await api.get(
                    PRESTATIONS_API,
                    {
                        params: {
                            categorie_id:
                                selectedCategorieId
                        }
                    }
                );

            const data =
                Array.isArray(
                    response.data
                )
                    ? response.data
                    : Array.isArray(
                        response.data?.data
                    )
                        ? response.data.data
                        : [];

            setPrestations(
                data
            );

        } catch (error) {

            console.error(
                "Erreur chargement prestations:",
                error
            );

            toast.error(
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Impossible de charger les prestations."
            );

            setPrestations([]);

        } finally {

            setLoadingPrestations(
                false
            );
        }
    };

    useEffect(() => {

        loadPrestations();

    }, [
        selectedCategorieId
    ]);

    // ========================================================
    // CATÉGORIE : AJOUT
    // ========================================================

    const openAddCategorie = () => {

        setEditingCategorie(
            null
        );

        setCategorieNom("");

        setCategorieModal(
            true
        );
    };

    // ========================================================
    // CATÉGORIE : MODIFICATION
    // ========================================================

    const openEditCategorie = (
        categorie
    ) => {

        if (
            String(
                categorie.nom
            ).trim().toLowerCase() ===
            CATEGORIE_HEBERGEMENT
                .toLowerCase()
        ) {

            toast.error(
                "La catégorie Hébergement est gérée par les modules dédiés."
            );

            return;
        }

        setEditingCategorie(
            categorie
        );

        setCategorieNom(
            categorie.nom || ""
        );

        setCategorieModal(
            true
        );
    };

    // ========================================================
    // CATÉGORIE : FERMER
    // ========================================================

    const closeCategorieModal = () => {

        if (
            savingCategorie
        ) {
            return;
        }

        setCategorieModal(
            false
        );

        setEditingCategorie(
            null
        );

        setCategorieNom("");
    };

    // ========================================================
    // CATÉGORIE : ENREGISTRER
    // ========================================================

    const saveCategorie = async (
        event
    ) => {

        event.preventDefault();

        const nom =
            categorieNom.trim();

        if (!nom) {

            toast.error(
                "Le nom de la catégorie est obligatoire."
            );

            return;
        }

        if (
            nom.toLowerCase() ===
            CATEGORIE_HEBERGEMENT
                .toLowerCase()
        ) {

            toast.error(
                "La catégorie Hébergement est réservée aux modules d'hébergement."
            );

            return;
        }

        setSavingCategorie(
            true
        );

        try {

            if (
                editingCategorie
            ) {

                await api.put(
                    `${CATEGORIES_API}/${editingCategorie.id}`,
                    {
                        nom
                    }
                );

                toast.success(
                    "Catégorie modifiée."
                );

            } else {

                const response =
                    await api.post(
                        CATEGORIES_API,
                        {
                            nom
                        }
                    );

                toast.success(
                    "Catégorie créée."
                );

                const newCategory =
                    response.data?.data ||
                    response.data?.category ||
                    response.data;

                if (
                    newCategory?.id
                ) {

                    setSelectedCategorieId(
                        newCategory.id
                    );
                }
            }

            closeCategorieModal();

            await loadCategories();

        } catch (error) {

            console.error(
                "Erreur catégorie:",
                error
            );

            toast.error(
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Impossible d'enregistrer la catégorie."
            );

        } finally {

            setSavingCategorie(
                false
            );
        }
    };

    // ========================================================
    // CATÉGORIE : SUPPRESSION
    // ========================================================

    const deleteCategorie = async (
        categorie
    ) => {

        if (
            String(
                categorie.nom
            ).trim().toLowerCase() ===
            CATEGORIE_HEBERGEMENT
                .toLowerCase()
        ) {

            toast.error(
                "La catégorie Hébergement ne peut pas être supprimée."
            );

            return;
        }

        const confirmation =
            window.confirm(
                `Supprimer la catégorie "${categorie.nom}" ?\n\nCette opération ne sera possible que si aucune prestation n'y est rattachée.`
            );

        if (!confirmation) {
            return;
        }

        try {

            await api.delete(
                `${CATEGORIES_API}/${categorie.id}`
            );

            toast.success(
                "Catégorie supprimée."
            );

            if (
                Number(
                    selectedCategorieId
                ) ===
                Number(
                    categorie.id
                )
            ) {

                setSelectedCategorieId(
                    null
                );
            }

            await loadCategories();

        } catch (error) {

            console.error(
                "Erreur suppression catégorie:",
                error
            );

            toast.error(
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Impossible de supprimer la catégorie."
            );
        }
    };

    // ========================================================
    // PRESTATION : AJOUT
    // ========================================================

    const openAddPrestation = () => {

        if (
            !selectedCategorieId
        ) {
            toast.error(
                "Sélectionnez une catégorie."
            );

            return;
        }

        setEditingPrestation(
            null
        );

        setPrestationForm({
            categorie_id:
                selectedCategorieId,
            nom: "",
            nom_fr: "",
            nom_en: "",
            prix_defaut: "",
            actif: true
        });

        setPrestationModal(
            true
        );
    };

    // ========================================================
    // PRESTATION : MODIFICATION
    // ========================================================

    const openEditPrestation = (
        prestation
    ) => {

        setEditingPrestation(
            prestation
        );

        setPrestationForm({
            categorie_id:
                prestation.categorie_id ||
                selectedCategorieId,
            nom:
                prestation.nom ||
                "",
            nom_fr:
                prestation.nom_fr ||
                "",
            nom_en:
                prestation.nom_en ||
                "",
            prix_defaut:
                prestation.prix_defaut ??
                "",
            actif:
                prestation.actif !== false
        });

        setPrestationModal(
            true
        );
    };

    // ========================================================
    // PRESTATION : FERMER
    // ========================================================

    const closePrestationModal = () => {

        if (
            savingPrestation
        ) {
            return;
        }

        setPrestationModal(
            false
        );

        setEditingPrestation(
            null
        );

        setPrestationForm({
            categorie_id: "",
            nom: "",
            nom_fr: "",
            nom_en: "",
            prix_defaut: "",
            actif: true
        });
    };

    // ========================================================
    // PRESTATION : FORM
    // ========================================================

    const updatePrestationForm = (
        field,
        value
    ) => {

        setPrestationForm(
            previous => ({
                ...previous,
                [field]: value
            })
        );
    };

    // ========================================================
    // PRESTATION : ENREGISTRER
    // ========================================================

    const savePrestation = async (
        event
    ) => {

        event.preventDefault();

        const nom =
            prestationForm.nom.trim();

        const categorieId =
            Number(
                prestationForm.categorie_id
            );

        const prix =
            Number(
                prestationForm.prix_defaut
            );

        if (!categorieId) {

            toast.error(
                "Sélectionnez une catégorie."
            );

            return;
        }

        if (!nom) {

            toast.error(
                "La désignation est obligatoire."
            );

            return;
        }

        if (
            !Number.isFinite(
                prix
            ) ||
            prix < 0
        ) {

            toast.error(
                "Le prix par défaut est invalide."
            );

            return;
        }

        // ----------------------------------------------------
        // Sécurité frontend supplémentaire :
        // ne jamais permettre de créer/modifier une prestation
        // dans Hébergement.
        // ----------------------------------------------------

        const categorie =
            categories.find(
                category =>
                    Number(
                        category.id
                    ) ===
                    categorieId
            );

        if (
            categorie &&
            String(
                categorie.nom
            ).trim().toLowerCase() ===
            CATEGORIE_HEBERGEMENT
                .toLowerCase()
        ) {

            toast.error(
                "Les prestations d'Hébergement sont gérées ailleurs."
            );

            return;
        }

        setSavingPrestation(
            true
        );

        const payload = {

            categorie_id:
                categorieId,

            nom,

            nom_fr:
                prestationForm.nom_fr
                    .trim() || null,

            nom_en:
                prestationForm.nom_en
                    .trim() || null,

            prix_defaut:
                Number(
                    prix.toFixed(2)
                ),

            actif:
                Boolean(
                    prestationForm.actif
                )
        };

        try {

            if (
                editingPrestation
            ) {

                await api.put(
                    `${PRESTATIONS_API}/${editingPrestation.id}`,
                    payload
                );

                toast.success(
                    "Prestation modifiée."
                );

            } else {

                await api.post(
                    PRESTATIONS_API,
                    payload
                );

                toast.success(
                    "Prestation créée."
                );
            }

            closePrestationModal();

            await loadPrestations();

        } catch (error) {

            console.error(
                "Erreur prestation:",
                error
            );

            toast.error(
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Impossible d'enregistrer la prestation."
            );

        } finally {

            setSavingPrestation(
                false
            );
        }
    };

    // ========================================================
    // PRESTATION : SUPPRIMER
    // ========================================================

    const deletePrestation = async (
        prestation
    ) => {

        const confirmation =
            window.confirm(
                `Supprimer définitivement "${prestation.nom}" ?\n\nSi cette prestation a déjà été utilisée dans des opérations, il est préférable de la désactiver plutôt que de la supprimer.`
            );

        if (!confirmation) {
            return;
        }

        try {

            await api.delete(
                `${PRESTATIONS_API}/${prestation.id}`
            );

            toast.success(
                "Prestation supprimée."
            );

            await loadPrestations();

        } catch (error) {

            console.error(
                "Erreur suppression prestation:",
                error
            );

            toast.error(
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Impossible de supprimer la prestation."
            );
        }
    };

    // ========================================================
    // PRESTATION : ACTIVER / DÉSACTIVER
    // ========================================================

    const togglePrestation = async (
        prestation
    ) => {

        try {

            await api.put(
                `${PRESTATIONS_API}/${prestation.id}`,
                {
                    actif:
                        !prestation.actif
                }
            );

            toast.success(
                prestation.actif
                    ? "Prestation désactivée."
                    : "Prestation activée."
            );

            await loadPrestations();

        } catch (error) {

            console.error(
                "Erreur changement statut:",
                error
            );

            toast.error(
                error.response?.data?.error ||
                error.response?.data?.message ||
                "Impossible de modifier le statut."
            );
        }
    };

    // ========================================================
    // FORMAT PRIX
    // ========================================================

    const formatPrix = (
        value
    ) => {

        return Number(
            value || 0
        ).toLocaleString(
            "fr-FR",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        );
    };

    // ========================================================
    // RENDER
    // ========================================================

    return (
        <div className="p-4 md:p-6 space-y-6">

            {/* ==================================================
                EN-TÊTE
            ================================================== */}

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                <div>

                    <div className="flex items-center gap-3">

                        <div className="p-3 rounded-2xl bg-gray-100">
                            <Package
                                size={25}
                            />
                        </div>

                        <div>

                            <h1 className="text-2xl font-bold">
                                Gestion des prestations
                            </h1>

                            <p className="text-sm text-gray-500">
                                Catalogue des services complémentaires
                            </p>

                        </div>

                    </div>

                </div>

                <button
                    type="button"
                    onClick={() => {
                        loadCategories();
                        loadPrestations();
                    }}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200"
                >

                    <RefreshCw
                        size={17}
                    />

                    Actualiser

                </button>

            </div>

            {/* ==================================================
                CONTENU
            ================================================== */}

            <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5">

                {/* =================================================
                    CATÉGORIES
                ================================================= */}

                <div className="bg-white rounded-2xl shadow overflow-hidden">

                    <div className="p-4 border-b flex items-center justify-between">

                        <div>

                            <h2 className="font-bold">
                                Catégories
                            </h2>

                            <p className="text-xs text-gray-500 mt-1">
                                {
                                    categoriesVisibles.length
                                }{" "}
                                catégorie
                                {categoriesVisibles.length !== 1
                                    ? "s"
                                    : ""}
                            </p>

                        </div>

                        <button
                            type="button"
                            onClick={
                                openAddCategorie
                            }
                            className="p-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
                            title="Ajouter une catégorie"
                        >

                            <Plus
                                size={18}
                            />

                        </button>

                    </div>

                    {loadingCategories ? (

                        <div className="p-6 text-center text-gray-500">
                            Chargement...
                        </div>

                    ) : (

                        <div className="p-2">

                            {categoriesVisibles.map(
                                category => {

                                    const active =
                                        Number(
                                            selectedCategorieId
                                        ) ===
                                        Number(
                                            category.id
                                        );

                                    return (
                                        <div
                                            key={
                                                category.id
                                            }
                                            className={`flex items-center rounded-xl mb-1 ${
                                                active
                                                    ? "bg-gray-900 text-white"
                                                    : "hover:bg-gray-100"
                                            }`}
                                        >

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setSelectedCategorieId(
                                                        category.id
                                                    )
                                                }
                                                className="flex-1 min-w-0 text-left px-3 py-3"
                                            >

                                                <span className="font-medium truncate block">
                                                    {
                                                        category.nom
                                                    }
                                                </span>

                                            </button>

                                            <div className="flex items-center gap-1 pr-2">

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        openEditCategorie(
                                                            category
                                                        )
                                                    }
                                                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                                                        active
                                                            ? "hover:bg-white/10"
                                                            : "hover:bg-gray-200"
                                                    }`}
                                                >
                                                    Modifier
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        deleteCategorie(
                                                            category
                                                        )
                                                    }
                                                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                                                        active
                                                            ? "text-red-300 hover:bg-white/10"
                                                            : "text-red-600 hover:bg-red-50"
                                                    }`}
                                                >
                                                    Supprimer
                                                </button>

                                            </div>

                                        </div>
                                    );
                                }
                            )}

                        </div>

                    )}

                </div>

                {/* =================================================
                    PRESTATIONS
                ================================================= */}

                <div className="bg-white rounded-2xl shadow overflow-hidden">

                    <div className="p-4 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-3">

                        <div>

                            <h2 className="text-xl font-bold">
                                {
                                    selectedCategorie?.nom ||
                                    "Sélectionnez une catégorie"
                                }
                            </h2>

                            {selectedCategorie && (
                                <p className="text-sm text-gray-500 mt-1">
                                    Produits et services de cette catégorie.
                                </p>
                            )}

                        </div>

                        {selectedCategorie && (

                            <button
                                type="button"
                                onClick={
                                    openAddPrestation
                                }
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
                            >

                                <Plus
                                    size={17}
                                />

                                Ajouter une prestation

                            </button>

                        )}

                    </div>

                    {!selectedCategorie ? (

                        <div className="p-10 text-center text-gray-500">
                            Sélectionnez une catégorie.
                        </div>

                    ) : loadingPrestations ? (

                        <div className="p-10 text-center text-gray-500">
                            Chargement...
                        </div>

                    ) : prestations.length === 0 ? (

                        <div className="p-10 text-center">

                            <Package
                                size={35}
                                className="mx-auto text-gray-400"
                            />

                            <p className="mt-3 text-gray-600">
                                Aucune prestation dans cette catégorie.
                            </p>

                            <button
                                type="button"
                                onClick={
                                    openAddPrestation
                                }
                                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
                            >

                                <Plus
                                    size={17}
                                />

                                Ajouter la première prestation

                            </button>

                        </div>

                    ) : (

                        <div className="overflow-x-auto">

                            <table className="w-full">

                                <thead className="bg-gray-50">

                                    <tr>

                                        <th className="text-left px-5 py-3 text-sm font-semibold">
                                            Désignation
                                        </th>

                                        <th className="text-left px-5 py-3 text-sm font-semibold">
                                            Prix par défaut
                                        </th>

                                        <th className="text-left px-5 py-3 text-sm font-semibold">
                                            Statut
                                        </th>

                                        <th className="text-right px-5 py-3 text-sm font-semibold">
                                            Actions
                                        </th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {prestations.map(
                                        prestation => (

                                            <tr
                                                key={
                                                    prestation.id
                                                }
                                                className="border-t"
                                            >

                                                <td className="px-5 py-4">

                                                    <div className="font-semibold">
                                                        {
                                                            prestation.nom
                                                        }
                                                    </div>

                                                    {prestation.nom_en && (
                                                        <div className="text-xs text-gray-400 mt-1">
                                                            {
                                                                prestation.nom_en
                                                            }
                                                        </div>
                                                    )}

                                                </td>

                                                <td className="px-5 py-4 font-semibold">

                                                    {
                                                        formatPrix(
                                                            prestation.prix_defaut
                                                        )
                                                    }{" "}
                                                    USD

                                                </td>

                                                <td className="px-5 py-4">

                                                    {prestation.actif !== false ? (

                                                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                                            Actif
                                                        </span>

                                                    ) : (

                                                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                                                            Inactif
                                                        </span>

                                                    )}

                                                </td>

                                                <td className="px-5 py-4">

                                                    <div className="flex items-center justify-end gap-2">

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                openEditPrestation(
                                                                    prestation
                                                                )
                                                            }
                                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border hover:bg-gray-50"
                                                        >

                                                            <Pencil
                                                                size={15}
                                                            />

                                                            Modifier

                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                togglePrestation(
                                                                    prestation
                                                                )
                                                            }
                                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border hover:bg-gray-50"
                                                        >

                                                            <Power
                                                                size={15}
                                                            />

                                                            {prestation.actif !== false
                                                                ? "Désactiver"
                                                                : "Activer"}

                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                deletePrestation(
                                                                    prestation
                                                                )
                                                            }
                                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                                                        >

                                                            <Trash2
                                                                size={15}
                                                            />

                                                            Supprimer

                                                        </button>

                                                    </div>

                                                </td>

                                            </tr>

                                        )
                                    )}

                                </tbody>

                            </table>

                        </div>

                    )}

                </div>

            </div>

            {/* ==================================================
                MODAL CATÉGORIE
            ================================================== */}

            {categorieModal && (

                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">

                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">

                        <div className="p-5 border-b flex items-center justify-between">

                            <h2 className="text-lg font-bold">
                                {
                                    editingCategorie
                                        ? "Modifier la catégorie"
                                        : "Nouvelle catégorie"
                                }
                            </h2>

                            <button
                                type="button"
                                onClick={
                                    closeCategorieModal
                                }
                                className="p-2 rounded-lg hover:bg-gray-100"
                            >

                                <X
                                    size={19}
                                />

                            </button>

                        </div>

                        <form
                            onSubmit={
                                saveCategorie
                            }
                        >

                            <div className="p-5">

                                <label className="block text-sm font-semibold mb-2">
                                    Nom de la catégorie
                                </label>

                                <input
                                    type="text"
                                    value={
                                        categorieNom
                                    }
                                    onChange={(e) =>
                                        setCategorieNom(
                                            e.target.value
                                        )
                                    }
                                    placeholder="Ex. Café & Bar"
                                    maxLength={100}
                                    autoFocus
                                    className="w-full border rounded-xl px-3 py-3 outline-none focus:ring-2 focus:ring-blue-400"
                                />

                            </div>

                            <div className="p-5 border-t flex justify-end gap-2">

                                <button
                                    type="button"
                                    onClick={
                                        closeCategorieModal
                                    }
                                    className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200"
                                >
                                    Annuler
                                </button>

                                <button
                                    type="submit"
                                    disabled={
                                        savingCategorie
                                    }
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                >

                                    <Save
                                        size={17}
                                    />

                                    {savingCategorie
                                        ? "Enregistrement..."
                                        : "Enregistrer"}

                                </button>

                            </div>

                        </form>

                    </div>

                </div>

            )}

            {/* ==================================================
                MODAL PRESTATION
            ================================================== */}

            {prestationModal && (

                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">

                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

                        <div className="p-5 border-b flex items-center justify-between">

                            <div>

                                <h2 className="text-lg font-bold">
                                    {
                                        editingPrestation
                                            ? "Modifier la prestation"
                                            : "Nouvelle prestation"
                                    }
                                </h2>

                                <p className="text-xs text-gray-500 mt-1">
                                    Le prix proposé pourra être modifié lors de l'encaissement.
                                </p>

                            </div>

                            <button
                                type="button"
                                onClick={
                                    closePrestationModal
                                }
                                className="p-2 rounded-lg hover:bg-gray-100"
                            >

                                <X
                                    size={19}
                                />

                            </button>

                        </div>

                        <form
                            onSubmit={
                                savePrestation
                            }
                        >

                            <div className="p-5 space-y-4">

                                <div>

                                    <label className="block text-sm font-semibold mb-2">
                                        Catégorie
                                    </label>

                                    <select
                                        value={
                                            prestationForm.categorie_id
                                        }
                                        onChange={(e) =>
                                            updatePrestationForm(
                                                "categorie_id",
                                                e.target.value
                                            )
                                        }
                                        className="w-full border rounded-xl px-3 py-3 bg-white"
                                    >

                                        <option value="">
                                            Sélectionner une catégorie
                                        </option>

                                        {categoriesVisibles.map(
                                            category => (

                                                <option
                                                    key={
                                                        category.id
                                                    }
                                                    value={
                                                        category.id
                                                    }
                                                >
                                                    {
                                                        category.nom
                                                    }
                                                </option>

                                            )
                                        )}

                                    </select>

                                </div>

                                <div>

                                    <label className="block text-sm font-semibold mb-2">
                                        Désignation
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            prestationForm.nom
                                        }
                                        onChange={(e) =>
                                            updatePrestationForm(
                                                "nom",
                                                e.target.value
                                            )
                                        }
                                        placeholder="Ex. Café"
                                        maxLength={150}
                                        className="w-full border rounded-xl px-3 py-3"
                                    />

                                </div>

                                <div>

                                    <label className="block text-sm font-semibold mb-2">
                                        Nom français
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            prestationForm.nom_fr
                                        }
                                        onChange={(e) =>
                                            updatePrestationForm(
                                                "nom_fr",
                                                e.target.value
                                            )
                                        }
                                        placeholder="Ex. Café"
                                        maxLength={150}
                                        className="w-full border rounded-xl px-3 py-3"
                                    />

                                </div>

                                <div>

                                    <label className="block text-sm font-semibold mb-2">
                                        Nom anglais
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            prestationForm.nom_en
                                        }
                                        onChange={(e) =>
                                            updatePrestationForm(
                                                "nom_en",
                                                e.target.value
                                            )
                                        }
                                        placeholder="Ex. Coffee"
                                        maxLength={150}
                                        className="w-full border rounded-xl px-3 py-3"
                                    />

                                </div>

                                <div>

                                    <label className="block text-sm font-semibold mb-2">
                                        Prix par défaut
                                    </label>

                                    <div className="flex">

                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={
                                                prestationForm.prix_defaut
                                            }
                                            onChange={(e) =>
                                                updatePrestationForm(
                                                    "prix_defaut",
                                                    e.target.value
                                                )
                                            }
                                            placeholder="0.00"
                                            className="flex-1 border rounded-l-xl px-3 py-3"
                                        />

                                        <span className="flex items-center px-4 border border-l-0 rounded-r-xl bg-gray-50 font-semibold text-gray-500">
                                            USD
                                        </span>

                                    </div>

                                </div>

                                <label className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 cursor-pointer">

                                    <input
                                        type="checkbox"
                                        checked={
                                            prestationForm.actif
                                        }
                                        onChange={(e) =>
                                            updatePrestationForm(
                                                "actif",
                                                e.target.checked
                                            )
                                        }
                                    />

                                    <div>

                                        <div className="font-semibold text-sm">
                                            Prestation active
                                        </div>

                                        <div className="text-xs text-gray-500">
                                            Une prestation inactive ne sera pas proposée au Prestataire.
                                        </div>

                                    </div>

                                </label>

                            </div>

                            <div className="p-5 border-t flex justify-end gap-2">

                                <button
                                    type="button"
                                    onClick={
                                        closePrestationModal
                                    }
                                    className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200"
                                >
                                    Annuler
                                </button>

                                <button
                                    type="submit"
                                    disabled={
                                        savingPrestation
                                    }
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                >

                                    <Save
                                        size={17}
                                    />

                                    {savingPrestation
                                        ? "Enregistrement..."
                                        : "Enregistrer"}

                                </button>

                            </div>

                        </form>

                    </div>

                </div>

            )}

        </div>
    );
}